import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Field, ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import SvgIcon from 'src/helpers/SvgIcon/SvgIcon';
import ImagePrimary from 'src/helpers/Media/ImagePrimary';
import { RichTextWrapper } from '../RichTextWrapper';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';

interface TooltipProps {
  fields?: {
    tooltipText?: Field<string>;
    tooltipImage?: ImageField;
  };
}

const Tooltip = (props: TooltipProps) => {
  const [tooltipLocation, setTooltipLocation] = useState({
    top: '0',
    bottom: '20px',
    left: '-288px',
  });
  const { currentScreenWidth } = useCurrentScreenType();
  const [isMobile, setIsMobile] = useState(currentScreenWidth <= getBreakpoint('mmd'));
  const [visible, setVisible] = useState('');

  const toolTipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMobile(currentScreenWidth <= getBreakpoint('mmd'));
  }, [currentScreenWidth]);

  if (!props.fields?.tooltipText?.value) {
    return <></>;
  }

  const showTooltip = () => {
    if (toolTipRef.current) {
      const tooltipElement = toolTipRef.current?.lastChild as HTMLDivElement;

      if (tooltipElement.getBoundingClientRect().width === 0) {
        return;
      }

      let _tooltipLocation = tooltipLocation;

      const toolTipTopCoordinate = tooltipElement?.getBoundingClientRect().top;

      const toolTipLeftCoordinate = tooltipElement?.getBoundingClientRect().left;

      const defaultCoor = toolTipLeftCoordinate + 288;

      // For tooltip placement, if it can fit on screen to right with 30px padding, flyout right
      // If it can fit left with 30px padding, flyout left
      // If neither, flyout left with adjustment to start of left edge and 30px padding
      _tooltipLocation = {
        top: toolTipTopCoordinate <= 0 ? '20px' : '0',
        bottom: toolTipTopCoordinate > 0 ? '20px' : '0',
        left:
          defaultCoor + tooltipElement.getBoundingClientRect().width > window.outerWidth - 30
            ? defaultCoor - tooltipElement.getBoundingClientRect().width < 30
              ? `${
                  -tooltipElement.getBoundingClientRect().width +
                  (30 - (defaultCoor - tooltipElement.getBoundingClientRect().width))
                }px`
              : `-${tooltipElement.getBoundingClientRect().width}px`
            : '0',
      };

      setTooltipLocation({ ..._tooltipLocation });
    }
  };

  const hideTooltip = () => {
    setVisible('');
    setTooltipLocation({ ...{ top: '0', bottom: '20px', left: '-288px' } });
  };

  return (
    <div
      ref={toolTipRef}
      className="group relative ml-xxs md:inline-flex"
      onMouseEnter={() => {
        if (!isMobile) {
          showTooltip();
        }
      }}
      onClick={() => {
        if (isMobile) {
          if (visible) {
            setVisible('');
          } else {
            setVisible('group-hover:visible');
          }
          showTooltip();
        }
      }}
      onMouseLeave={hideTooltip}
    >
      <SvgIcon icon="tooltip" className="cursor-pointer" />
      <div
        style={{
          top: tooltipLocation.top !== '0' ? tooltipLocation.top : undefined,
          bottom: tooltipLocation.bottom !== '0' ? tooltipLocation.bottom : undefined,
          left: tooltipLocation.left,
        }}
        className={classNames(
          'invisible absolute left-[-288px] z-[1000] w-max min-w-[86px] max-w-[288px] rounded-[8px] bg-white p-xxs text-body text-black opacity-0 shadow-[0_2px_5px_0px_rgba(0,0,0,0.25)] group-hover:opacity-100 md:group-hover:visible',
          visible
        )}
      >
        <RichTextWrapper field={props.fields?.tooltipText} classes="" />
        <ImagePrimary
          imageLayout="responsive"
          {...{
            fields: {
              primaryImage: { ...props.fields.tooltipImage },
              primaryImageMobile: { ...props.fields.tooltipImage },
              primaryImageCaption: {
                value: '',
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default Tooltip;
